import Phaser from 'phaser';
import { SCENE, GAME_WIDTH, GAME_HEIGHT, placementSize, STREET_SPAWNS } from '../config.js';
import { api } from '../api/client.js';
import { gameState } from '../state/gameState.js';
import { currentPhase } from '../state/dayCycle.js';
import { MoneyBar } from '../ui/MoneyBar.js';
import { SceneTransition, EDGE_THRESHOLD } from '../ui/SceneTransition.js';
import { WalkController } from '../ui/WalkController.js';
import { NatalkaSprite } from '../ui/NatalkaSprite.js';
import { AnimalsController } from '../ui/AnimalsController.js';
import { openAnimalActions } from '../ui/animalActions.js';
import { BikeController } from '../ui/BikeController.js';
import { reportLocation } from '../ui/locationTracker.js';
import { showModal } from '../ui/Modal.js';
import { upgradeButton } from '../ui/gameUi.js';
import { showToast } from '../ui/Toast.js';
import { messageFor } from '../api/errors.js';
import { attachCoordsDebug } from '../ui/CoordsDebug.js';
import { attachSceneEditMode } from '../ui/SceneEditMode.js';
import { attachFriendsButton } from '../ui/FriendsButton.js';
import { realtime } from '../api/realtime.js';
import { attachRemotePresence } from '../ui/remotePresence.js';
import { attachRoomChat } from '../ui/ChatInput.js';
import { StairsController } from '../ui/StairsController.js';
import { mainFloorFor, porchSpawn, stairNodesFor, MAIN_SPAWN_FEET_Y, MAIN_BIKE_PARKED_DEFAULTS } from './mainLayout.js';
import { OUTDOOR_CHARACTER_SCALE, PERSPECTIVE } from '../ui/carCatalog.mjs';
import { centreForFeet, feetForCentre, fitCharacter } from '../ui/outdoorScale.mjs';
import { BACKYARD_SPAWN } from './backyardLayout.js';
import { PlacementHud } from '../ui/PlacementHud.js';
import { placementTexture } from '../ui/seating.js';
import { attachPlacementMenu } from '../ui/placementInteraction.js';
import { applyPlacementSize, applyPlacementFlip } from '../ui/placementSizing.js';
import { ySortDepth } from '../ui/ySort.js';
import { attachYardLook } from './yardLook.js';
import { queueMissing, ensureLoaded, loadCtx, prioritiseFor } from '../ui/sceneLoading.js';
import { needsFor } from '../assetManifest.mjs';
import { preloader } from './PreloadScene.js';
import { iconText } from '../ui/iconText.js';
import { homeArrival } from './homeLayout.mjs';

export class MainScene extends Phaser.Scene {
  constructor() { super(SCENE.MAIN); }

  preload() {
    // Staged loading: only what this scene draws now, only if the cache lacks it.
    queueMissing(this, needsFor(this.scene.key, loadCtx()));
    prioritiseFor(this.scene.key);
  }

  create(data) {
    // Spawn position — z data?.spawnX/spawnY (kierunek z którego przyszedł lub przywrócona pozycja)
    // The yard is drawn at the street's real-world scale (a 1.2 m child is 120 px on the
    // reference plane, growing towards the viewer) — she used to be twice the door's height.
    // Everything person-shaped reads these two fields: the avatar, peers, pets, the bike.
    this.worldScale = OUTDOOR_CHARACTER_SCALE;
    this.perspective = PERSPECTIVE;
    // spawnY is a sprite centre (a restored position); spawnFeetY is where the feet go — a porch spot moves to where
    // this level's porch is drawn (porchSpawn).
    const feet = porchSpawn(this._level(), data?.spawnX ?? GAME_WIDTH / 2, data?.spawnFeetY ?? MAIN_SPAWN_FEET_Y);
    const spawnX = data?.spawnY != null ? (data?.spawnX ?? GAME_WIDTH / 2) : feet.x;
    const spawnY = data?.spawnY ?? centreForFeet(this, feet.feetY);
    // The yard's picture of this time of day and house level under the live sky (scenes/yardLook.js).
    this._yard = attachYardLook(this, { side: 'front', phase: currentPhase(), level: () => this._level() });

    // Lock on the house — above the yard picture, which fades in over it (depth 0.01) and then stays at depth 0 after it
    // in the display list (yardLook.js)
    this.lock = this.add.image(GAME_WIDTH / 2 + 200, GAME_HEIGHT - 320, 'icon_lock').setDisplaySize(80, 80).setDepth(1);
    this._refreshLockVisibility();
    this.tweens.add({ targets: this.lock, y: this.lock.y - 10, yoyo: true, repeat: -1, duration: 800, ease: 'Sine.easeInOut' });

    // Natalka
    this.natalka = new NatalkaSprite(this, spawnX, spawnY, { worldScale: this.worldScale });
    this.natalka.setOutfit(gameState.raw?.inventory?.equipped);
    this.natalka.setDepth(10);  // always in front of the parked bike (depth 1)
    this.walker = new WalkController(this, this.natalka, { floorPolyline: mainFloorFor(this._level()), onBroadcast: (p) => realtime.move(p) });
    fitCharacter(this);
    reportLocation(this, this.natalka.x, this.natalka.y, { immediate: true });

    // Wandering animals whose buda is placed in the front yard ("exterior").
    this.animals = new AnimalsController(this, {
      sceneId: 'exterior',
      ownerId: window.__myId ?? null,
      getPlayers: () => {
        const players = [];
        if (window.__myId) players.push({ id: window.__myId, x: this.natalka.x, y: this.natalka.y });
        if (this._presence) for (const [id, av] of this._presence.avatars) players.push({ id, x: av.sprite.x, y: av.sprite.y });
        return players;
      },
    });
    this.events.on('shutdown', () => this.animals.destroy());

    // Climb the stairs to enter the house (mechanic shared with VisitMainScene).
    this.stairs = new StairsController(this, {
      natalka: this.natalka,
      walker: this.walker,
      nodes: () => stairNodesFor(this._level()),   // the porch of the house as it is now
      onMove: (x, y) => reportLocation(this, x, y),
      onBroadcast: (p) => realtime.move(p),   // others watch you climb, as they watch you walk
      onEnterHouse: () => this._goHome(),
    });

    // Bike — parked in front of the house; rideable here (and in town).
    this.bike = new BikeController(this, {
      natalka: this.natalka,
      floor: { floorPolyline: mainFloorFor(this._level()) },   // the rider shares the walker's ground
      parkedDefaults: MAIN_BIKE_PARKED_DEFAULTS,
      onBroadcast: (p) => realtime.move(p),
    });
    this._bikeBlockToastAt = 0;

    // Transitions
    this.transition = new SceneTransition(this);
    this.transition.addExitZone({ side: 'left',  toScene: SCENE.OUTSIDE, label: '{arrow_left} Miasto', data: { spawnX: STREET_SPAWNS.HOUSE } });
    // House is entered by climbing the stairs (see StairsController / _goHome), not the right edge.
    // Podwórko lives off the right edge — only once the house is unlocked.
    this._backyardExitAdded = false;
    this._bikeExitPending = false;
    this._bikeExitRearm = false;   // declined while riding: ride away before re-offering
    this._ensureBackyardExit();

    new MoneyBar(this);

    attachFriendsButton(this);

    // Upgrade house: a chunky game button under the coins (owner 2026-09-30: "Ulepsz should just
    // look better"). "Wyloguj się" moved to Ustawienia (ui/SettingsModal.js, the gear); "Od nowa" is gone.
    this.upgradeBtn = upgradeButton(this, '{house} Ulepsz dom', () => this._onUpgradeClick());
    this._refreshUpgradeBtn();

    this._poll();
    this.pollTimer = this.time.addEvent({ delay: 30000, loop: true, callback: () => this._poll() });
    this._unsub = gameState.on(() => {
      this._applyState();
      this._refreshUpgradeBtn();
      this._ensureBackyardExit();
    });
    this.events.on('shutdown', () => { this._unsub?.(); this.pollTimer?.remove(); });

    attachCoordsDebug(this);
    if (import.meta.env?.DEV) attachSceneEditMode(this);  // dev-only foreground/walk authoring (press E)

    // Decorations placed in the front yard (backend scene "exterior").
    this._decorSprites = new Map();
    this._renderDecorations();
    new PlacementHud(this);

    if (window.__myId) this._presence = attachRemotePresence(this, `house:${window.__myId}:${this.scene.key}`);
    if (window.__myId) attachRoomChat(this, `house:${window.__myId}:${this.scene.key}`);
  }

  update(_t, dt) {
    this._yard.update(currentPhase(), dt);   // live phase (honors the T/R dev override)
    if (this.bike.isRiding) {
      this.bike.update(dt);
      this._checkBikeExits();
    } else {
      this.stairs.update();
      if (!this.stairs.isOnStairs) {
        this.walker.update(dt);
        this.transition.checkExits(this.natalka);
      }
      fitCharacter(this);   // her size follows where she stands (y-perspective)
    }
    this.bike?.syncBikeAction();
    this.animals.update(dt);
    const m = this.bike.isRiding ? this.bike.rider : this.natalka;
    reportLocation(this, m.x, m.y);
    this._depthSort();
  }

  _renderDecorations() {
    for (const sp of this._decorSprites.values()) sp.destroy();
    this._decorSprites.clear();
    const placements = (gameState.raw?.placements ?? []).filter((p) => p.scene === 'exterior');
    for (const p of placements) {
      const tex = placementTexture(p);
      const sprite = this.add.image(p.x, p.y, this.textures.exists(tex) ? tex : 'plot_empty')
        .setInteractive({ useHandCursor: true });
      if (!this.textures.exists(tex)) preloader.whenReady(tex, () => { if (sprite.active) { sprite.setTexture(tex); applyPlacementSize(sprite, p.item); } });
      applyPlacementSize(sprite, p.item);
      applyPlacementFlip(sprite, p);
      attachPlacementMenu(this, sprite, p);
      this._decorSprites.set(p.id, sprite);
      if (p.type === 'buda') this.animals.attachBudaClick(sprite, p);
    }
  }

  async _onAnimalClick(a, opts = {}) {
    return openAnimalActions(this, a, { ...opts, onPetHeart: () => this.animals.sprites.get(a.id)?.showPetHeart() });
  }

  // y-sort the avatar with the placed decorations so you walk in front/behind them.
  _depthSort() {
    this.natalka.setDepth(ySortDepth(feetForCentre(this, this.natalka.y)));
    // On the bike the rider sprite stands in for the character: sort it by its wheel
    // line like everything else, or it stays at the bike's parked depth (1) and every
    // lamp post and bush placed in the yard draws over it.
    if (this.bike?.isRiding) this.bike.rider.setDepth(ySortDepth(this.bike.rider.y + this.bike.riderWheelOffset));
    for (const sp of this._decorSprites.values()) sp.setDepth(ySortDepth(sp.y + sp.displayHeight / 2));
    if (this._presence) {
      for (const a of this._presence.avatars.values()) a.sprite.setDepth(ySortDepth(a.groundY ?? feetForCentre(this, a.sprite.y)));
    }
  }

  // While riding: left edge → ride to town; right edge → ride into the Podwórko (once the
  // house is unlocked; the locked-yard modal still applies). The house is reached on foot.
  _checkBikeExits() {
    const r = this.bike.rider;
    if (r.x <= 150) { this.scene.start(SCENE.OUTSIDE, { spawnX: STREET_SPAWNS.HOUSE }); return; }
    // Same "must leave the zone first" rule as SceneTransition: after declining the unlock
    // the rider is still at the right edge, so don't re-offer until she has ridden away.
    if (this._bikeExitRearm) {
      if (r.x < GAME_WIDTH - 150 - 40) this._bikeExitRearm = false;
      return;
    }
    if (r.x >= GAME_WIDTH - 150 && this._backyardExitAdded && !this._bikeExitPending) {
      this._bikeExitPending = true;
      this._goBackyard().then((ok) => {
        this._bikeExitPending = false;
        if (ok) this.scene.start(SCENE.BACKYARD, { spawnX: BACKYARD_SPAWN.x + 60, spawnFeetY: BACKYARD_SPAWN.feetY });
        else this._bikeExitRearm = true;
      });
    }
  }

  // The right edge leads to the Podwórko. It appears once the house is unlocked (the
  // backyard is "behind" the house); walking into it while the yard is still locked
  // offers the 500💰 unlock, like the house padlock.
  _ensureBackyardExit() {
    if (this._backyardExitAdded || gameState.raw?.house?.locked !== false) return;
    this._backyardExitAdded = true;
    this.transition.addExitZone({
      side: 'right', toScene: SCENE.BACKYARD, label: 'Podwórko {arrow_right}',
      data: { spawnX: BACKYARD_SPAWN.x, spawnFeetY: BACKYARD_SPAWN.feetY },
      onTrigger: () => this._goBackyard(),
    });
  }

  // Resolves true to let the transition proceed, false to stay (cancelled / failed).
  async _goBackyard() {
    if (!gameState.raw?.backyard?.locked) return true;
    const cost = gameState.raw?.catalog?.backyard_unlock_cost ?? 500;
    const ok = await showModal(this, {
      title: 'Podwórko jest zamknięte {lock}',
      body: `Czy chcesz otworzyć podwórko za ${cost} {coin}?\n(Masz: ${gameState.raw.money} {coin})`,
      confirmLabel: 'Otwórz', cancelLabel: 'Nie',
    });
    if (!ok) { this._stepOutOfBackyardExit(); return false; }
    try {
      await gameState.unlockBackyard();
      showToast(this, 'Podwórko otwarte! {party}');
      return true;
    } catch (e) {
      showToast(this, messageFor(e));
      this._stepOutOfBackyardExit();
      return false;
    }
  }

  // Declined (or couldn't afford) the unlock: input was locked while the modal was up, so
  // she is still standing in the right-edge exit zone. SceneTransition won't re-fire until
  // she leaves it — but the position is also saved by reportLocation, so a reload would
  // resume inside the zone. Step her out so the next entry starts clean.
  _stepOutOfBackyardExit() {
    this.natalka.x = GAME_WIDTH - EDGE_THRESHOLD - 40;
    if (this.bike?.isRiding) this.bike.rider.x = GAME_WIDTH - 200;
  }

  // Climbing past the top step enters the house (StairsController.onEnterHouse).
  async _goHome() {
    if (gameState.raw?.house?.locked) {
      const ok = await showModal(this, {
        title: 'Dom jest zamknięty {lock}',
        body: `Czy chcesz otworzyć dom za 20 {coin}?\n(Masz: ${gameState.raw.money} {coin})`,
        confirmLabel: 'Otwórz', cancelLabel: 'Nie',
      });
      if (!ok) return;
      try {
        await gameState.unlockHouse();
        showToast(this, 'Dom otwarty! {party}');
      } catch (e) {
        showToast(this, messageFor(e));
        return;
      }
    }
    this.scene.start(SCENE.HOME, homeArrival('front'));   // inside the front door, a step towards the middle of the hall
  }

  _level() { return gameState.raw?.house?.level ?? 1; }

  _applyState() {
    // (the yard picture follows the level by itself: scenes/yardLook.js)
    // An upgrade can bring a new porch, and with it new ground (mainFloorFor).
    this.walker?.setFloorPolyline(mainFloorFor(this._level()));
    this.bike?.riderWalker?.setFloorPolyline(mainFloorFor(this._level()));
    this._refreshLockVisibility();
    this.natalka?.setOutfit(gameState.raw?.inventory?.equipped);
    this.bike?.refresh();
    this._renderDecorations();
  }

  _refreshLockVisibility() {
    const locked = gameState.raw?.house?.locked ?? true;
    this.lock.setVisible(locked);
  }

  _refreshUpgradeBtn() {
    const h = gameState.raw?.house;
    if (!h || !this.upgradeBtn) return;
    this.upgradeBtn.setLabel(h.is_max_level ? '{castle} Dom na max!' : `{house} Ulepsz · ${h.next_upgrade_cost} {coin}`);
  }

  async _onUpgradeClick() {
    const h = gameState.raw?.house;
    if (!h) return;
    if (h.is_max_level) {
      showToast(this, 'Dom jest już maksymalny! {castle}');
      return;
    }
    const ok = await showModal(this, {
      title: 'Ulepszyć dom?',
      body: `Poziom ${h.level} {arrow_right} ${h.level + 1}\nKoszt: ${h.next_upgrade_cost} {coin}\n(Masz: ${gameState.raw.money} {coin})`,
      confirmLabel: 'Ulepsz', cancelLabel: 'Nie',
    });
    if (!ok) return;
    try {
      // gameState._apply() applies the new state and fires listeners — including the one
      // registered in create() that calls this._applyState() — SYNCHRONOUSLY as part of
      // resolving upgradeHouse()'s own promise, i.e. before this `await` unblocks. So by
      // the time control reaches the next line the new level is wanted; with its art not yet
      // cached the yard would keep the old level until it arrives (yardLook.js levelToDraw).
      // Load the new level's art against a *projected* next-level state BEFORE calling
      // upgradeHouse(), so it's already cached when the listener fires.
      const nextCtx = loadCtx({ state: { ...gameState.raw, house: { ...h, level: h.level + 1 } } });
      await ensureLoaded(this, needsFor(this.scene.key, nextCtx));
      await gameState.upgradeHouse();
      prioritiseFor(this.scene.key);   // …and its other phases next
      showToast(this, `{party} Dom poziom ${gameState.raw.house.level}!`);
    } catch (e) {
      showToast(this, messageFor(e));
    }
  }

  async _poll() { try { await gameState.refresh(); } catch (_) { /* a failed poll is harmless — the next tick tries again */ } }
}
